/**
 * Status Badge Mixin
 * Provides shared methods for status badge display and color mapping
 */
export default {
  methods: {
    /**
     * Maps a status string to a CSS color class
     * @param {string} status - The status string
     * @returns {string} - CSS color class name (success, info, warning, error, primary, grey)
     */
    getStatusColor(status) {
      if (!status) return "grey";

      const colorMap = {
        new: "success",
        pending: "warning",
        overdue: "error",
        completed: "success",
        submitted: "info",
        "in review": "info",
        approved: "success",
        rejected: "error",
        reviewed: "success",
        open: "warning",
        assigned: "info",
        "in progress": "primary",
        resolved: "success",
        closed: "grey",
        cancelled: "grey",
        deleted: "error",
      };

      return colorMap[status.toLowerCase()] || "grey";
    },

    /**
     * Maps a status string to a translation key
     * @param {string} status - The status string
     * @returns {string} - Translation key (e.g., "t_open", "t_completed")
     */
    getStatusDisplayName(status) {
      if (!status) return "";

      const statusKeyMap = {
        new: "t_new",
        pending: "t_pending",
        overdue: "t_overdue",
        completed: "t_completed",
        submitted: "t_completed",
        "in review": "t_in_review",
        approved: "t_approved",
        rejected: "t_rejected",
        reviewed: "t_reviewed",
        open: "t_open",
        assigned: "t_assigned",
        "in progress": "t_in_progress",
        resolved: "t_resolved",
        closed: "t_closed",
        "on hold": "t_on_hold",
        cancelled: "t_cancelled",
        deleted: "t_deleted",
      };

      const key = statusKeyMap[status.toLowerCase()];
      return key ? this.$t(key) : status.charAt(0).toUpperCase() + status.slice(1);
    },

    /**
     * Maps issue status code to CSS color class
     * @param {string} status - Issue status code (o, a, p, h, r, c, d)
     * @returns {string} - CSS color class name
     */
    getIssueStatusColor(status) {
      if (!status || status === "d") return "grey"; // Don't show deleted status
      // Normalize legacy "n" status to "o" (Open)
      if (status === "n") status = "o";

      const colorMap = {
        o: "warning", // Open - orange
        a: "info", // Assigned - blue
        p: "primary", // In Progress - blue
        h: "warning", // On Hold - orange
        r: "success", // Resolved - green
        c: "grey", // Closed - grey
      };

      return colorMap[status.toLowerCase()] || "grey";
    },

    /**
     * Maps issue status code to display name
     * @param {string} status - Issue status code (o, a, p, h, r, c, d)
     * @returns {string} - Translated display name
     */
    getIssueStatusDisplayName(status) {
      if (!status || status === "d") return ""; // Don't show deleted status
      // Normalize legacy "n" status to "o" (Open)
      if (status === "n") status = "o";

      const statusKeyMap = {
        o: "t_open",
        a: "t_assigned",
        p: "t_in_progress",
        h: "t_on_hold",
        r: "t_resolved",
        c: "t_closed",
      };

      const key = statusKeyMap[status.toLowerCase()];
      return key ? this.$t(key) : status.toUpperCase();
    },

    /**
     * Maps planner status code to CSS color class
     * @param {number} status - Planner status code (0-5)
     * @returns {string} - CSS color class name
     */
    getPlannerStatusColor(status) {
      // Map planner status (0-5) to consistent color classes
      // 0 = Ready to Start / Not Started, 1 = In Progress, 2 = Completed, 3 = Cancelled, 4 = Deleted, 5 = Overdue
      switch (status) {
        case 0:
          return "grey"; // ready to start / not started
        case 1:
          return "primary"; // in progress - blue
        case 2:
          return "success"; // completed - green
        case 3:
          return "grey"; // cancelled
        case 4:
          return "error"; // deleted - red
        case 5:
          return "error"; // overdue - red
        default:
          return "grey";
      }
    },
  },
};
